import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { UserService } from '../../../user/user.service';

function dedupePaths(paths: Array<{ path_id: number; name: string }>): Array<{ path_id: number; name: string }> {
  const byId = new Map<number, { path_id: number; name: string }>();
  for (const p of paths) {
    if (!Number.isFinite(p.path_id) || p.path_id <= 0) continue;
    if (!byId.has(p.path_id)) byId.set(p.path_id, p);
  }
  return [...byId.values()];
}

function isPlaceholderPathLabel(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  const v = String(value).trim().toLowerCase();
  if (v.length === 0) return true;
  return [
    'unknown path',
    'unknown',
    'unavngivet uddannelsessti',
    'unavngivet',
    'ukendt uddannelsessti',
    'ukendt',
    'ukendt sti',
    'unavngivet sti',
    'null',
    'undefined'
  ].includes(v);
}

/** Prefer short `name`, then `full_name`. Ignore backend placeholders like "Unknown Path". */
function displayPathName(path: { name?: unknown; full_name?: unknown; fullName?: unknown } | null | undefined): string {
  const name = path?.name;
  const fullName = path?.full_name ?? path?.fullName;
  const candidateName = isPlaceholderPathLabel(name) ? '' : String(name).trim();
  const candidateFullName = isPlaceholderPathLabel(fullName) ? '' : String(fullName).trim();
  return candidateName || candidateFullName || 'Ukendt sti';
}

/** Same shapes as profile + fallback på objekt-værdier uden brugbare nøgler (path_id kun på objektet). */
function pathsFromEducationalPaths(educationalPaths: unknown): Array<{ path_id: number; name: string }> {
  if (!educationalPaths) {
    return [];
  }
  if (Array.isArray(educationalPaths)) {
    return dedupePaths(
      educationalPaths.map((p: any) => ({
        path_id: Number(p?.path_id ?? p?.pathId),
        name: displayPathName(p)
      }))
    );
  }
  if (typeof educationalPaths === 'object') {
    const o = educationalPaths as Record<string, any>;
    const fromKeys = Object.keys(o).map(pathIdKey => {
      const pathData = o[pathIdKey];
      const path_id = parseInt(pathIdKey, 10) || Number(pathData?.path_id ?? pathData?.pathId);
      return {
        path_id,
        name: displayPathName(pathData)
      };
    });
    const fromVals = Object.values(o)
      .filter((v): v is Record<string, any> => v != null && typeof v === 'object' && !Array.isArray(v))
      .map((p: any) => ({
        path_id: Number(p?.path_id ?? p?.pathId),
        name: displayPathName(p)
      }));
    return dedupePaths([...fromKeys, ...fromVals]);
  }
  return [];
}

/** API kan returnere rå array eller envelope — undgå tom liste ved `{ data: [...] }` osv. */
function normalizeManualMinutesList(body: unknown): unknown[] {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (typeof body === 'object') {
    const o = body as Record<string, unknown>;
    const nested = o['data'] ?? o['items'] ?? o['result'] ?? o['rows'] ?? o['records'];
    if (Array.isArray(nested)) return nested;
  }
  return [];
}

export function profile_manual_maintenance_minutes(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  router: Router,
  userService: UserService
) {
  const fetchManualMinutes = (
    userUuid: string,
    statusYear: number,
    params: LoadDataParams | undefined,
    http: HttpClient
  ) => {
    // Samme år som profilen: eksplicit værdi (kalenderår når ?year= mangler — ikke server-default).
    return userService.getUserByUuid(userUuid, statusYear).pipe(
      catchError(userError => {
        console.error('[manual_maintenance_minutes] Error fetching user by UUID:', userError);
        // Return empty data structure so component can still show for admins
        return of({ uuid: userUuid } as any);
      }),
      switchMap(userData => {
        // Get user ID from the UserDTO
        const userId = userData?.id;
        
        if (!userId) {
          console.warn('[manual_maintenance_minutes] User ID not found in response, but continuing with empty data:', userData);
          // Return empty data but with user_uuid so component can still show for admins
          return of({ items: [], totalCount: 0, user_uuid: userUuid, user_id: null });
        }
        
        // Extract paths from user status (educational_paths in status object)
        // The user status contains educational_paths which is an object with path_id as keys
        const userStatus = (userData as any)?.status;
        const educationalPaths = userStatus?.educational_paths;

        const pathsData = pathsFromEducationalPaths(educationalPaths);
        
        
        // If no paths from status, return empty data
        if (!pathsData || pathsData.length === 0) {
          return of({ items: [], totalCount: 0, user_uuid: userUuid, user_id: userId });
        }
        
        // Process paths data
        return of(pathsData).pipe(
          switchMap(pathsData => {

            const pathNameById = new Map<number, string>();
            for (const p of pathsData) {
              if (p.path_id > 0) {
                pathNameById.set(p.path_id, p.name || 'Ukendt sti');
              }
            }

            const bulkUrl = `${environment.apiUrl}/paths/users/${userId}/manual-minutes`;

            return http.get<any>(bulkUrl).pipe(
              map((body: any) => {
                const list = normalizeManualMinutesList(body);
                const allMinutes: any[] = list.map((minute: any) => {
                  const pathId = Number(minute?.path_id);
                  const pathName =
                    minute?.path_name ||
                    pathNameById.get(pathId) ||
                    'Ukendt sti';
                  return { ...minute, path_name: pathName };
                });

                // Group by year, then by path
                const grouped: { [year: number]: { [pathId: number]: any[] } } = {};
                allMinutes.forEach(minute => {
                  const year = minute.year;
                  const pathId = minute.path_id;
                  
                  if (!grouped[year]) {
                    grouped[year] = {};
                  }
                  if (!grouped[year][pathId]) {
                    grouped[year][pathId] = [];
                  }
                  grouped[year][pathId].push(minute);
                });

                // Convert to flat structure for display
                const items: any[] = [];
                Object.keys(grouped).sort((a, b) => parseInt(b) - parseInt(a)).forEach(yearStr => {
                  const year = parseInt(yearStr);
                  const yearData = grouped[year];
                  
                  Object.keys(yearData).forEach(pathIdStr => {
                    const pathId = parseInt(pathIdStr);
                    const pathMinutes = yearData[pathId];
                    const pathName = pathMinutes[0]?.path_name || 'Ukendt sti';
                    
                    // Calculate totals for this path-year (module credits + extra minutes)
                    const totalMinutes = pathMinutes.reduce((sum: number, m: any) => {
                      if (m.acquire_all_needed) {
                        return sum;
                      }
                      if (m.total_credited_minutes != null) {
                        return sum + Number(m.total_credited_minutes);
                      }
                      const moduleTotal = Array.isArray(m.completed_modules)
                        ? m.completed_modules.reduce((s: number, mod: any) => s + (Number(mod.maintenance_minutes_credited) || 0), 0)
                        : Number(m.module_minutes_total) || 0;
                      return sum + (Number(m.maintenance_minutes) || 0) + moduleTotal;
                    }, 0);
                    
                    items.push({
                      year,
                      path_id: pathId,
                      path_name: pathName,
                      total_minutes: totalMinutes,
                      entries: pathMinutes,
                      entry_count: pathMinutes.length
                    });
                  });
                });

                // Extract unique paths from pathsData for the response
                const userPaths = pathsData.map((p: any) => ({
                  path_id: p.path_id,
                  path_name: p.name
                }));

                const response = {
                  items,
                  totalCount: items.length,
                  pageSize: items.length,
                  pageIndex: 0,
                  user_uuid: userUuid,
                  user_id: userId,
                  user_paths: userPaths
                };
                return response;
              }),
              catchError(err => {
                if (err?.status !== 400 && err?.status !== 403) {
                  console.warn('[manual_maintenance_minutes] Could not load manual minutes', err?.status ?? err);
                }
                return of({ items: [], totalCount: 0, user_uuid: userUuid, user_id: userId, user_paths: pathsData.map((p: any) => ({
                  path_id: p.path_id,
                  path_name: p.name
                })) });
              })
            );
          }),
          catchError(err => {
            console.error('[manual_maintenance_minutes] Error fetching paths:', err);
            return of({ items: [], totalCount: 0, user_uuid: userUuid, user_id: userId });
          })
        );
      }),
      catchError(error => {
        console.error('[manual_maintenance_minutes] Error fetching user:', error);
        // Return empty data structure so component can still show for admins
        return of({ items: [], totalCount: 0, user_uuid: userUuid, user_id: null });
      })
    );
  };

  return {
    name: 'Manuelle Vedligeholdelsesminutter',
    description: 'Oversigt over manuelle vedligeholdelsesminutter for en bruger',
    execute: (params?: LoadDataParams) => {

      const resolveStatusYear = (): number => {
        const raw = params?.queryParams?.['year'] ?? router.parseUrl(router.url).queryParams['year'];
        if (raw === undefined || raw === null || raw === '') {
          return new Date().getFullYear();
        }
        const y = parseInt(String(raw), 10);
        return Number.isNaN(y) ? new Date().getFullYear() : y;
      };
      const statusYear = resolveStatusYear();
      
      // Extract user UUID from params or current route
      let userUuid = params?.queryParams?.['userUuid'];
      
      if (!userUuid) {
        // Try to get UUID from current route if we're on a profile page
        const currentUrl = window.location.pathname;
        const profileMatch = currentUrl.match(/\/profile\/([a-f0-9-]{36})/);
        if (profileMatch) {
          userUuid = profileMatch[1];
        }
      }
      
      // If still no UUID, try to get it from UserService (for current user's own profile)
      if (!userUuid) {
        
        // First try the URL-based method from UserService
        userUuid = userService.getCurrentUserUuid();
        
        if (!userUuid) {
          // If that fails, get it from the current user token
          return userService.getCurrentUserFromToken().pipe(
            switchMap(currentUser => {
              if (!currentUser || !currentUser.uuid) {
                console.error('No current user or UUID available');
                return of({ items: [], totalCount: 0 });
              }
              
              return fetchManualMinutes(currentUser.uuid, statusYear, params, http);
            }),
            catchError(error => {
              console.error('Error getting current user from token:', error);
              return of({ items: [], totalCount: 0 });
            })
          );
        }
      }
      
      if (!userUuid) {
        console.error('No user UUID provided for manual maintenance minutes');
        return of({ items: [], totalCount: 0 });
      }
      
      return fetchManualMinutes(userUuid, statusYear, params, http);
    },
    settings: (() => {
      return {
        columns: [],
        pageable: false,
        sortable: false,
        rowHover: false,
        searchable: false
      };
    })()
  };
}

